import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import DirectionsOutlinedIcon from '@material-ui/icons/DirectionsOutlined'
import OpenInNewOutlinedIcon from '@material-ui/icons/OpenInNewOutlined'
import { GoogleMap, InfoWindow, Marker } from '@react-google-maps/api'
import ButtonCta from '@components/atoms/button-cta'
import { useLoadScripts } from '@lib/google/places/browser'
import {
  COACH_PROFILE_META_ICON_PROPS,
  ExternalLink,
  MapPin,
} from '@lib/pga-coach/coach-profile-icons'
import { PGA_COACH_SEARCH_MAP_STYLES } from '@components/pga-coach-search-map/map-styles'

const MAP_BASE_BACKGROUND = '#f4f5f6'

const GOOGLE_MAP_OPTIONS = {
  streetViewControl: false,
  fullscreenControl: false,
  mapTypeControl: false,
  scaleControl: true,
  clickableIcons: false,
  backgroundColor: MAP_BASE_BACKGROUND,
  styles: PGA_COACH_SEARCH_MAP_STYLES,
  gestureHandling: 'cooperative',
}

const FACILITY_MARKER_ICON = {
  path: 'M 0,0 m -10,0 a 10,10 0 1,0 20,0 a 10,10 0 1,0 -20,0',
  fillColor: '#143d66',
  fillOpacity: 1,
  strokeColor: '#ffffff',
  strokeOpacity: 1,
  strokeWeight: 2,
  scale: 1.3,
}

function buildGoogleMapsDestination({ mapSearchAddress, center }) {
  if (center?.lat != null && center?.lng != null) {
    return `${center.lat},${center.lng}`
  }
  return mapSearchAddress
}

export function googleMapsDirectionsUrl({ mapSearchAddress, center }) {
  const destination = buildGoogleMapsDestination({ mapSearchAddress, center })
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`
}

export function googleMapsPlaceUrl({ mapSearchAddress, center }) {
  const query = buildGoogleMapsDestination({ mapSearchAddress, center })
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
}

function renderMapActionIcon(profilePillActions, ProfileIcon, LegacyIcon) {
  const Icon = profilePillActions ? ProfileIcon : LegacyIcon
  return profilePillActions
    ? <Icon {...COACH_PROFILE_META_ICON_PROPS} />
    : <Icon fontSize="small" aria-hidden />
}

function MapActionLinks({
  directionsUrl,
  placeUrl,
  classes,
  buttonClassName = '',
  profilePillActions = false,
}) {
  const openExternal = (url) => (event) => {
    event.preventDefault()
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const buttonClassNames = [classes.mapActionButton, buttonClassName].filter(Boolean).join(' ')
  const outlinedButtonClassNames = [
    classes.mapActionButtonOutlined,
    buttonClassName,
  ].filter(Boolean).join(' ')

  const primaryVariant = profilePillActions ? 'contained' : 'contained'
  const secondaryVariant = profilePillActions ? 'text' : 'outlined'
  const buttonSize = profilePillActions ? 'medium' : 'small'

  return (
    <div className={classes.mapActions}>
      <ButtonCta
        externalLink={directionsUrl}
        color="primary"
        variant={primaryVariant}
        size={buttonSize}
        className={buttonClassNames}
        startIcon={renderMapActionIcon(profilePillActions, MapPin, DirectionsOutlinedIcon)}
        onClick={openExternal(directionsUrl)}
      >
        Get directions
      </ButtonCta>
      <ButtonCta
        externalLink={placeUrl}
        variant={secondaryVariant}
        size={buttonSize}
        className={profilePillActions ? outlinedButtonClassNames : `${classes.mapActionButton} ${outlinedButtonClassNames}`}
        startIcon={renderMapActionIcon(profilePillActions, ExternalLink, OpenInNewOutlinedIcon)}
        onClick={openExternal(placeUrl)}
      >
        Open in Google Maps
      </ButtonCta>
    </div>
  )
}

function isUsableMapCenter(mapCenter) {
  return mapCenter != null
    && mapCenter.lat != null
    && mapCenter.lng != null
    && Number(mapCenter.lat) !== 0
    && Number(mapCenter.lng) !== 0
    && !Number.isNaN(Number(mapCenter.lat))
    && !Number.isNaN(Number(mapCenter.lng))
}

function CoachFacilityMap({
  facilityName,
  mapSearchAddress,
  classes,
  mapCenter = null,
  showActions = true,
  header = null,
  zoom = 15,
  profilePillActions = false,
}) {
  const { isLoaded, loadError } = useLoadScripts()
  const hasProvidedCenter = isUsableMapCenter(mapCenter)
  const [center, setCenter] = useState(() => (
    hasProvidedCenter
      ? { lat: Number(mapCenter.lat), lng: Number(mapCenter.lng) }
      : null
  ))
  const [geocodeState, setGeocodeState] = useState(() => (
    hasProvidedCenter ? 'resolved' : 'pending'
  ))
  const [infoWindowOpen, setInfoWindowOpen] = useState(false)

  const mapTitle = useMemo(
    () => `${facilityName} Facility Location`,
    [facilityName],
  )

  const directionsUrl = useMemo(
    () => googleMapsDirectionsUrl({ mapSearchAddress, center }),
    [mapSearchAddress, center],
  )

  const placeUrl = useMemo(
    () => googleMapsPlaceUrl({ mapSearchAddress, center }),
    [mapSearchAddress, center],
  )

  useEffect(() => {
    if (hasProvidedCenter) {
      setCenter({ lat: Number(mapCenter.lat), lng: Number(mapCenter.lng) })
      setGeocodeState('resolved')
      return undefined
    }

    if (!isLoaded || !mapSearchAddress || typeof window === 'undefined') return undefined
    if (!window.google?.maps?.Geocoder) return undefined

    let cancelled = false
    setGeocodeState('pending')
    setCenter(null)
    const geocoder = new window.google.maps.Geocoder()

    geocoder.geocode({ address: mapSearchAddress }, (results, status) => {
      if (cancelled) return
      if (status === 'OK' && results?.[0]?.geometry?.location) {
        const { location } = results[0].geometry
        setCenter({ lat: location.lat(), lng: location.lng() })
        setGeocodeState('resolved')
        return
      }
      setGeocodeState('failed')
    })

    return () => {
      cancelled = true
    }
  }, [isLoaded, mapSearchAddress, hasProvidedCenter, mapCenter?.lat, mapCenter?.lng])

  const wrapCard = (body) => (
    <div className={classes.mapCard}>
      {header}
      {body}
    </div>
  )

  if (loadError) {
    return wrapCard(
      <>
        <div className={classes.mapWrap} aria-label={mapTitle}>
          <div className={classes.mapLoading}>We could not load the map right now.</div>
        </div>
        {showActions ? (
          <MapActionLinks
            directionsUrl={directionsUrl}
            placeUrl={placeUrl}
            classes={classes}
          />
        ) : null}
      </>,
    )
  }

  if (!isLoaded || geocodeState === 'pending') {
    return wrapCard(
      <div className={classes.mapWrap} aria-label={mapTitle}>
        <div className={classes.mapLoading}>Loading map...</div>
      </div>,
    )
  }

  if (geocodeState === 'failed' || !center) {
    return wrapCard(
      <>
        <div className={classes.mapWrap} aria-label={mapTitle}>
          <div className={classes.mapLoading}>
            We could not plot this address on the map, but you can still open it in Google Maps.
          </div>
        </div>
        {showActions ? (
          <MapActionLinks
            directionsUrl={directionsUrl}
            placeUrl={placeUrl}
            classes={classes}
          />
        ) : null}
      </>,
    )
  }

  return wrapCard(
    <>
      <div className={classes.mapWrap} aria-label={mapTitle}>
        <GoogleMap
          mapContainerClassName={classes.mapSurface}
          center={center}
          zoom={zoom}
          options={GOOGLE_MAP_OPTIONS}
        >
          <Marker
            position={center}
            icon={FACILITY_MARKER_ICON}
            title={facilityName}
            onClick={() => setInfoWindowOpen(true)}
          />
          {infoWindowOpen && (
            <InfoWindow
              position={center}
              onCloseClick={() => setInfoWindowOpen(false)}
              options={{ maxWidth: 280 }}
            >
              <div className={classes.mapInfoWindow}>
                <p className={classes.mapInfoWindowTitle}>{facilityName}</p>
                <div className={classes.mapInfoWindowActions}>
                  <ButtonCta
                    externalLink={directionsUrl}
                    color="primary"
                    variant="contained"
                    size="small"
                    className={`${classes.mapActionButton} ${classes.mapInfoWindowButton}`}
                    startIcon={renderMapActionIcon(profilePillActions, MapPin, DirectionsOutlinedIcon)}
                    onClick={(event) => {
                      event.preventDefault()
                      window.open(directionsUrl, '_blank', 'noopener,noreferrer')
                    }}
                  >
                    Get directions
                  </ButtonCta>
                  <ButtonCta
                    externalLink={placeUrl}
                    variant="outlined"
                    size="small"
                    className={`${classes.mapActionButton} ${classes.mapActionButtonOutlined} ${classes.mapInfoWindowButton}`}
                    startIcon={renderMapActionIcon(profilePillActions, ExternalLink, OpenInNewOutlinedIcon)}
                    onClick={(event) => {
                      event.preventDefault()
                      window.open(placeUrl, '_blank', 'noopener,noreferrer')
                    }}
                  >
                    Open in Google Maps
                  </ButtonCta>
                </div>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      </div>
      {showActions ? (
        <MapActionLinks
          directionsUrl={directionsUrl}
          placeUrl={placeUrl}
          classes={classes}
          profilePillActions={profilePillActions}
        />
      ) : null}
    </>,
  )
}

CoachFacilityMap.propTypes = {
  facilityName: PropTypes.string.isRequired,
  mapSearchAddress: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
  mapCenter: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
  showActions: PropTypes.bool,
  header: PropTypes.node,
  zoom: PropTypes.number,
  profilePillActions: PropTypes.bool,
}

export default CoachFacilityMap
